// Envio de documento direto ao Azure Blob (privado) — ver docs/API.md, "Documentos".
//   1. POST /documents/uploads → registro em `uploading` + URL SAS de escrita
//   2. PUT no Blob (Put Blob até 64 MB; acima, Put Block de 8 MB + Put Block List)
//   3. SHA-256 no navegador (crypto.subtle) → POST /documents/:id/complete { sha256, size_bytes }
// O apiClient só manda JSON e leva o cookie da sessão; aqui é XMLHttpRequest puro, SEM
// credenciais (a SAS é a autorização) — e com `upload.onprogress` para a barra de progresso.
// Sem @azure/storage-blob, para não inflar o bundle. Adaptado do mediaUpload.ts do marketingwebui.
import documentService from './documentService';
import type { Document, UploadRequest } from '@/types/document';
import {
  BLOCK_MAX_RETRIES,
  BLOCK_SIZE_BYTES,
  UploadCancelledError,
  blockIdFor,
  blockListXml,
  guessContentType,
  isUploadCancelled,
  planChunks,
  putBlockListUrl,
  putBlockUrl,
  sha256Hex,
  uploadModeFor,
  validateDocumentFile,
} from './documentUploadUtils';

export * from './documentUploadUtils';

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface UploadOptions {
  onProgress?: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

class BlobHttpError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'BlobHttpError';
    this.status = status;
  }
}

/** PUT cru no Blob com progresso. Rejeita com UploadCancelledError se o sinal abortar. */
const xhrPut = (
  url: string,
  body: Blob | string,
  headers: Record<string, string>,
  onProgress: (loaded: number) => void,
  signal?: AbortSignal,
): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadCancelledError());
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.withCredentials = false;
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    };
    const onAbort = () => xhr.abort();
    signal?.addEventListener('abort', onAbort);
    const cleanup = () => signal?.removeEventListener('abort', onAbort);
    xhr.onload = () => {
      cleanup();
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new BlobHttpError(xhr.status, `O armazenamento recusou o arquivo (HTTP ${xhr.status}).`));
    };
    xhr.onerror = () => {
      cleanup();
      reject(new BlobHttpError(0, 'Falha de rede ao enviar o arquivo.'));
    };
    xhr.onabort = () => {
      cleanup();
      reject(new UploadCancelledError());
    };
    xhr.send(body);
  });

const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = window.setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      window.clearTimeout(t);
      reject(new UploadCancelledError());
    });
  });

/** Sobe o conteúdo para a URL SAS (Put Blob ou blocos). Progresso em bytes enviados. */
export const uploadToBlob = async (
  file: Blob,
  sasUrl: string,
  contentType: string,
  { onProgress, signal }: UploadOptions = {},
): Promise<void> => {
  const total = file.size;
  const report = (loaded: number) => onProgress?.({ loaded: Math.min(loaded, total), total });

  if (uploadModeFor(total) === 'single') {
    await xhrPut(
      sasUrl,
      file,
      { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': contentType || 'application/octet-stream' },
      report,
      signal,
    );
    report(total);
    return;
  }

  const chunks = planChunks(total, BLOCK_SIZE_BYTES);
  const ids: string[] = [];
  let done = 0;
  for (const chunk of chunks) {
    const id = blockIdFor(chunk.index);
    const body = file.slice(chunk.start, chunk.end);
    for (let attempt = 0; ; attempt++) {
      try {
        await xhrPut(putBlockUrl(sasUrl, id), body, {}, (loaded) => report(done + loaded), signal);
        break;
      } catch (err) {
        if (isUploadCancelled(err) || attempt >= BLOCK_MAX_RETRIES) throw err;
        // 403 = SAS expirada/inválida: tentar de novo não resolve.
        if (err instanceof BlobHttpError && err.status === 403) throw err;
        report(done);
        await wait(1000 * (attempt + 1), signal);
      }
    }
    ids.push(id);
    done += chunk.end - chunk.start;
    report(done);
  }

  await xhrPut(
    putBlockListUrl(sasUrl),
    blockListXml(ids),
    { 'Content-Type': 'application/xml', 'x-ms-blob-content-type': contentType || 'application/octet-stream' },
    () => undefined,
    signal,
  );
  report(total);
};

/** Metadados do documento informados no formulário (o arquivo preenche nome, tipo e tamanho). */
export type DocumentUploadMeta = Omit<UploadRequest, 'file_name' | 'content_type' | 'size_bytes'>;

/**
 * Fluxo completo de um arquivo: valida → reserva → envio ao Blob → SHA-256 → confirmação.
 * Devolve o documento já `ready`.
 */
export const uploadDocumentFile = async (
  file: File,
  meta: DocumentUploadMeta,
  options: UploadOptions = {},
): Promise<Document> => {
  const problem = validateDocumentFile(file);
  if (problem) throw new Error(problem);
  const contentType = guessContentType(file.name, file.type) || 'application/octet-stream';
  const ticket = await documentService.createUpload({
    ...meta,
    file_name: file.name,
    content_type: contentType,
    size_bytes: file.size,
  });
  if (options.signal?.aborted) throw new UploadCancelledError();
  await uploadToBlob(file, ticket.upload_url, contentType, options);
  if (options.signal?.aborted) throw new UploadCancelledError();
  const sha256 = await sha256Hex(await file.arrayBuffer());
  return documentService.complete(ticket.document_id, sha256, file.size);
};
